<script lang="ts">
  // Renders a post's comments as threaded conversations. Comments are Skool-style
  // compact bubbles (see <SkoolComment>); nested reply levels are indented and
  // joined by a vertical connector line down their left, so it's clear which
  // comments sit at the same level of the same thread.
  //
  // The API returns a flat list (each with parent_id + depth), in *chronological*
  // order — NOT grouped parent-then-child. We rebuild the tree here from parent_id
  // and render it recursively, so replies always nest under their true parent
  // regardless of the incoming order.
  //
  // Optional reply affordances, driven by `<PostThread>`: `targetId` highlights the
  // comment that was opened ("replying to this"), `activeId` says which one the reply
  // box is currently under, and `composer` is rendered directly beneath that comment.
  // All default off, so a plain read-only render is unchanged.
  import type { Snippet } from 'svelte';
  import SkoolComment from './SkoolComment.svelte';
  import { buildThreadTree, type TreeNode } from './commentTree';
  import type { CommentItem } from './member';

  let {
    comments,
    compact = false,
    onReply = null,
    onLike = null,
    likedIds = null,
    deletedIds = null,
    activeId = null,
    now = Date.now(),
    targetId = null,
    waitingIds = null,
    composer = null,
  }: {
    comments: CommentItem[];
    /** Tighten the padding and the nesting indent for a narrow column — see
     *  `<SkoolPost>`'s `compact`, which this forwards nothing to but matches. */
    compact?: boolean;
    now?: number;
    /**
     * Reply to a DIFFERENT comment than the one that was opened.
     *
     * Every comment offers it, because "answer this one instead" is the ordinary case
     * and the alternative was: go back, find that comment's own row, open it. The
     * thread's owner holds which one is active and moves the single composer there.
     */
    onReply?: ((id: number) => void) | null;
    /** Like one comment. Absent where nothing can write to Skool. Never an unlike —
     *  see `<Upvote>`. */
    onLike?: ((id: number) => Promise<void> | void) | null;
    /** Skool ids the owner has already liked. Null means unknown, which renders the
     *  same as not-liked — see `lib/likes.ts` on why it can only ever be best-effort. */
    likedIds?: Set<string> | null;
    /** Skool ids this browser has just found to 404. An OVERLAY over each row's own
     *  `deleted_at` from the backend — see `<PostThread>`'s note. Null means "nothing
     *  extra known here", not "not deleted". */
    deletedIds?: Set<string> | null;
    /** Which comment the composer is currently under. Defaults to `targetId`. */
    activeId?: number | null;
    /** Highlight this comment; `composer` renders directly beneath it. */
    targetId?: number | null;
    /**
     * The comments still waiting on somebody. Given, every OTHER thread folds away
     * behind one control.
     *
     * A busy post is mostly finished business — a moderator already answered, or
     * nothing about it ever needed answering — and showing forty of those around the
     * two that do is how the one thing being asked for gets lost. Omit it and nothing
     * folds, which is what a plain reader wants.
     */
    waitingIds?: number[] | null;
    /** Reply composer, rendered directly beneath the comment being answered. */
    composer?: Snippet | null;
  } = $props();

  type Node = TreeNode<CommentItem>;

  // The route returns comments FLAT and chronological, nesting and all, so the tree
  // is rebuilt from `parent_id` — see `./commentTree`, which the inbox also walks so
  // its reply box moves in the order these are rendered.
  const roots = $derived(buildThreadTree(comments));

  /**
   * WHICH THREADS ARE SHOWN, and why there is only one rule for it.
   *
   * There used to be two collapses fighting each other: a hard "first 10 roots" cap,
   * and the reply target being pinned back into view when the cap had hidden it. The
   * cap answered the wrong question — length is not what makes a thread noisy, and
   * cutting at ten hid unanswered work while keeping answered work above it.
   *
   * What is shown now is what is still WAITING (plus whatever is being replied to,
   * always). Everything else folds behind one control. With no `waitingIds` nothing
   * folds at all, and a short thread never folds either — a control that hides two
   * comments costs more than it saves.
   */
  const FOLD_ABOVE = 5;
  let showAll = $state(false);

  function subtreeHas(node: Node, ids: Set<number>): boolean {
    return ids.has(node.id) || node.children.some((c) => subtreeHas(c, ids));
  }
  const keep = $derived.by(() => {
    const ids = new Set<number>(waitingIds ?? []);
    if (targetId != null) ids.add(targetId);
    if (activeId != null) ids.add(activeId);
    return ids;
  });
  const visibleRoots = $derived.by(() => {
    if (showAll || !waitingIds || roots.length <= FOLD_ABOVE) return roots;
    const wanted = roots.filter((r) => subtreeHas(r, keep));
    // Nothing on this post is waiting — fold to the newest few rather than to nothing,
    // so the card still reads as a conversation.
    return wanted.length ? wanted : roots.slice(-2);
  });
  const hiddenCount = $derived(roots.length - visibleRoots.length);

  /** How many replies to show on a comment's chip.
   *
   *  The TREE is the better source here and the server's `reply_count` is the fallback,
   *  which is the reverse of every other surface — this component holds the post's whole
   *  thread, so `children.length` is the exact number by construction, and it stays right
   *  the moment a reply is added to what is on screen. `reply_count` is what a caller
   *  that only has a slice of the thread has to trust. */
  function replyCount(node: Node): number | null {
    return node.children.length || (node.reply_count ?? null);
  }
</script>

{#snippet branch(nodes: Node[])}
  <div class="flex flex-col gap-4">
    {#each nodes as node (node.id)}
      <div>
        <!-- The row being answered: a ring around its own bubble, nothing more. The
             bubble is already the shape of a comment, so marking it needs no extra box.

             `data-targeted` is the hook a HOST needs to scroll the marked row into view.
             The ring says which comment is the subject; on a thirty-comment thread
             opened from a list, saying so below the fold says nothing. Marking it here
             rather than exposing an element ref keeps the DOM the only contract.

             THE FOCUS WASH MUST NOT EAT THE DELETED ONE. The wash forces the bubble's
             fill with `!important`, which beat `<SkoolComment>`'s red tint outright — a
             deleted row that happened to be the one being answered rendered plain blue
             and looked live. So on a deleted node the fill is left alone and focus is
             carried by the ring instead: red fill says what the row IS, blue ring says
             where the reader is, and neither has to lose. -->
        <div
          data-targeted={(activeId ?? targetId) === node.id ? 'true' : undefined}
          class={(activeId ?? targetId) === node.id
            ? node.deleted_at || (node.external_id && deletedIds?.has(node.external_id))
              ? '[&_.rounded-2xl]:ring-1 [&_.rounded-2xl]:ring-primary/45'
              : '[&_.rounded-2xl]:!bg-primary/10 [&_.rounded-2xl]:ring-1 [&_.rounded-2xl]:ring-primary/35'
            : ''}
        >
          <SkoolComment
            author={node.author}
            body={node.content}
            images={node.images}
            videos={node.videos}
            skoolUrl={node.url}
            upvotes={node.upvotes}
            createdAt={node.created_at}
            pinned={node.pinned ?? false}
            replyCount={replyCount(node)}
            onReply={onReply ? () => onReply(node.id) : null}
            onLike={onLike ? () => onLike(node.id) : null}
            liked={!!node.external_id && !!likedIds?.has(node.external_id)}
            deleted={!!node.deleted_at || (!!node.external_id && !!deletedIds?.has(node.external_id))}
            replyActive={(activeId ?? targetId) === node.id}
            {now}
          />
        </div>
        {#if composer && (activeId ?? targetId) === node.id}
          <!-- DIRECTLY UNDER THE COMMENT, above its existing replies — where Skool puts
               it, and where the thing being answered is still on screen. After the
               subtree it could sit a dozen replies below the comment it belongs to,
               which on a busy thread reads as replying to the last one instead.

               Indented to the BUBBLE's column, not the node's: a comment is an avatar
               (28px) plus a 10px gap plus its bubble, so a box starting at the avatar's
               left edge was wider than the thing it answered and lined up with
               nothing. -->
          <div class="ml-[2.375rem] mt-2">{@render composer()}</div>
        {/if}
        {#if node.children.length}
          <!-- INDENT, NO RULE. Skool nests replies by shifting them under the parent's
               bubble and nothing else; the connector line was one more vertical stroke
               per level on a screen that already had too many. The offset lands the
               reply's avatar under the parent's bubble edge (28px avatar + 10px gap). -->
          <div class="{compact ? 'ml-6 mt-3' : 'ml-9 mt-4'}">
            {@render branch(node.children)}
          </div>
        {/if}
      </div>
    {/each}
  </div>
{/snippet}

<!-- No card per thread. Threads are separated by space and by the connector under
     each one; a border around every root turned a conversation into a filing cabinet. -->
<div class="flex flex-col {compact ? 'gap-4' : 'gap-5'}">
  {#each visibleRoots as root (root.id)}
    {@render branch([root])}
  {/each}
  {#if hiddenCount > 0 && !showAll}
    <button
      class="self-start text-xs font-medium text-base-content/62 underline-offset-2 transition-colors hover:text-primary hover:underline"
      onclick={() => (showAll = true)}
    >Show {hiddenCount} answered comment{hiddenCount === 1 ? '' : 's'}</button>
  {/if}
</div>
